import HOOK from "../components/hooks"
import { COLUMN, COMPLIANCE, JARGON, POSITION, POSITION_ENUM } from '../modules/constants'

const getLabel = positionEnum => POSITION[positionEnum].NAME
const numComplianceTargets = 5

export const LEGEND = {
  [0]: {
    label: "",
    tooltipTitle: "",
  },
  //#region Roles
  [COLUMN.FROM]: {
    label: "First Appointed to Board",
  },
  [COLUMN.UNTIL]: {
    label: "Resigned from Board",
  },
  [COLUMN.TENURE]: {
    label: "Board Tenure",
  },
  [COLUMN.COMPENSATION]: {
    label: `${JARGON.TOTAL_REMUNERATION} (£)`,
  },
  [COLUMN.ROLE]: {
    label: JARGON.ROLE,
    tooltipTitle: "Role type of director or officer",
  },
  [COLUMN.OVERLAP]: {
    label: "Overlap",
  },
  [COLUMN.VIA_PERSON]: {
    label: `Via ${JARGON.PERSON}`,
  },
  [COLUMN.VIA_COMPANY]: {
    label: `At ${JARGON.COMPANY}`,
  },
  //#endregion Roles

  //#region People
  [COLUMN.AGE]: {
    label: "Age",
  },
  [COLUMN.DOB]: {
    label: "Date of Birth",
  },
  [COLUMN.NATIONALITY]: {
    label: JARGON.NATIONALITY,
  },
  [COLUMN.SEX]: {
    label: JARGON.SEX,
    tooltipTitle: "Gender of director or officer"
  },
  [COLUMN.RESIDENCE]: {
    label: JARGON.RESIDENCE,
  },
  [COLUMN.OFFICER]: {
    label: "Person",
    tooltipTitle: "Name of the director or secretary",
  },
  //#endregion People Table

  //#region  CORE
  [COLUMN.COMPANY]: {
    label: "Company",
  },
  [COLUMN.ACTIVITIES]: {
    label: "Activities",
  },
  [COLUMN.ADDRESS]: {
    label: "Address",
  },
  [COLUMN.CREATED]: {
    label: JARGON.COMPANY_TIMELINE.FOUNDED,
    tooltipTitle: "Date company was founded (incorporated)",
  },
  [COLUMN.CEASED]: {
    label: JARGON.COMPANY_TIMELINE.CEASED,
    tooltipTitle: "The date the company ended",
  },
  [COLUMN.NAME]: {
    label: "Company name",
    tooltipTitle: "",
  },
  [COLUMN.NUMBER]: {
    label: "Company number",
    tooltipTitle: "Company registration number",
  },
  [COLUMN.LOCATION]: {
    label: "Location",
    tooltipTitle: "Location of registered office",
  },
  [COLUMN.ORDER]: {
    label: "Match",
    tooltipTitle: "Order of matching to search string (1 is best)",
  },
  [COLUMN.MARKET_CAP]: {
    label: "Market Cap.(£M)",
    tooltipTitle: "The total value of all a company's shares of stock"
  },
  [COLUMN.PREVIOUS_NAMES]: {
    label: "Previous names",
  },
  [COLUMN.STATUS]: {
    label: "Company Status",
  },
  [COLUMN.COMPANY_TYPE]: {
    label: "Company type",
  },
  [COLUMN.EMPLOYEES]: {
    label: "Employees",
    tooltipTitle: "Number of employees",
  },
  [COLUMN.LSE_INDEX]: {
    label: "Index",
    tooltipTitle: "London Stock Exchange share index of which company is a constituent",
  },
  [COLUMN.EPIC]: {
    label: JARGON.EPIC,
    tooltipTitle: "Ticker on London Stock Exchange",
  },
  [COLUMN.EPICS]: {
    label: JARGON.EPICS,
    tooltipTitle: "Tickers on London Stock Exchange (current first)",
  },
  [COLUMN[HOOK.INDUSTRY]]: {
    label: JARGON.ICB_INDUSTRY,
  },
  [COLUMN.REVENUE]: {
    label: "Revenue (£M)",
  },
  [COLUMN.COUNTRY_OF_INCORPORATION]: {
    label: "Incorporated in",
    tooltipTitle: "Country of incorporation",
  },
  //#endregion CORE

  //#region GOVERNANCE
  [COLUMN.AGE_AVG]: {
    label: "Mean Age (years)",
    tooltipTitle: "Average (mean) age of board directors",
  },
  [COLUMN.AGE_SD]: {
    label: "Standard Deviation of Age (years)",
    tooltipTitle: "Standard deviation of ages of board directors; indicates age diversity",
  },
  [COLUMN.COMPLIANT]: {
    label: `Targets Complied With`,
    tooltipTitle: `How many of the ${numComplianceTargets} targets for non-exec, female and ethnicity has the company complied with?`,
  },
  [COLUMN.EM_DIRs]: {
    label: "Ethnic Minority Directors",
    tooltipTitle: "Number of ethnic minority directors - compliant if 1 or more - combined information from Annual Report and Parker Review",
  },
  [COLUMN.INDEP_NON_EXEC_9]: {
    label: "Ind. Non-Execs >9 yrs tenure",
    tooltipTitle: "Number of independent non-executive directors with tenure more than 9 years - compliant if 0",
  },
  [COLUMN.NON_BRIT]: {
    label: "Non-British Directors",
    tooltipTitle: "Number of directors who have a non-British nationality (including directors with multiple nationalities, one of which is British)",
  },
  [COLUMN.PARKER]: {
    label: "Meets Parker Review Target (Dec 2022)",
    tooltipTitle: "Has declared that, at December 2022, it had at least 1 ethnic minority director",
  },
  [COLUMN.PCNT_FEMALE]: {
    label: "Percent Female",
    tooltipTitle: `Proportion of board directors who are female - compliant if ${COMPLIANCE.PCNT.FEMALE}% or greater`,
  },
  [COLUMN.PCNT_INDEP_NON_EXECS]: {
    label: "Ind. Non-Execs (exc. Chair)",
    tooltipTitle: `Proportion of directors, excluding Chair, who are independent non-executives - compliant if ${COMPLIANCE.PCNT.NON_EXECS}% or greater`,
  },
  [COLUMN.SENIOR_FEMALE]: {
    label: "Senior Females",
    tooltipTitle: "Number of females in senior board roles: CEO, CFO, Chair, SID - compliant if 1 or more",
  },
  //#endregion GOVERNANCE

  //#region DATES
  [COLUMN.DAYS_BEFORE_PUBLICATION]: {
    label: "Days From End of FY to Annual Report Publication",
    tooltipTitle: "Number of days between end of last reported financial year and publication of Annual Report",
  },
  [COLUMN.DUE_DATE]: {
    label: "Next Annual Report Due",
    tooltipTitle: "Date when next Annual Report is due (4 months after financial year end for FTSE companies, 6 months for AIM)",
  },
  [COLUMN.END_OF_FY_OF_LAST_PUBLISHED_AR]: {
    label: "Last Annual Report End of FY",
    tooltipTitle: "End of financial year covered by last published Annual Report",
  },
  [COLUMN.LAST_AGM]: {
    label: "Last AGM",
    tooltipTitle: "Date of last Annual General Meeting",
  },
  [COLUMN.PUB_DATE_OF_LAST_PUBLISHED_AR]: {
    label: "Last Annual Report Publication Date",
    tooltipTitle: "Date of publication of last Annual Report",
  },
  //#endregion DATES

  //#region Adviser
  [COLUMN.AUDITOR]: {
    label: "Auditor",
    tooltipTitle: "The company's auditor"
  },

  [COLUMN.RECRUITING_ADVISER]: {
    label: "Recruiting Adviser",
    tooltipTitle: "The board's most recently disclosed recruiting adviser(s)",
  },
  [COLUMN.REMUNERATION_ADVISER]: {
    label: "Remuneration Adviser",
    tooltipTitle: "The board's most recently disclosed remuneration adviser",
  },

  [COLUMN.EXTERNAL_REVIEW]: {
    label: "Last External Review Facilitator",
    tooltipTitle: "The firm that facilitated the company's most recent externally facilitated board performance review, with a link to the relevant Annual Report",
  },

  [COLUMN.LAST]: {
    label: "Start of FY of Last External Review",
    tooltipTitle: "Start of FY of most recent externally facilitated board performance review",
  },

  [COLUMN.NEXT]: {
    label: "Start of FY of Next External Review",
    tooltipTitle: "Start of FY of expected next externally facilitated board performance review",
  },
  //#endregion Adviser

  //#region Advisers
  [COLUMN.AS_AUDITOR]: {
    label: "As Auditor",
    tooltipTitle: "As the company's auditor",
  },

  [COLUMN.AS_RECRUITING_ADVISER]: {
    label: "As Recruiting Adviser",
    tooltipTitle: "As the board's most recently disclosed executive search adviser",
  },

  [COLUMN.AS_REMUNERATION_ADVISER]: {
    label: "As Remuneration Adviser",
    tooltipTitle: "As the board's most recently disclosed remuneration adviser",
  },

  [COLUMN.AS_EXTERNAL_REVIEWER]: {
    label: "As External Review Facilitator",
    tooltipTitle: "As the firm that facilitated the company's most recently disclosed externally facilitated board performance review",
  },
  [COLUMN.TOTAL_CLIENTS]: {
    label: "Total Clients",
    tooltipTitle: "Total number of clients the adviser has across all categories (auditor, recruiting adviser, remuneration adviser, external review facilitator)",
    },
  //#endregion Advisers

  //#region BOARD
  [COLUMN.CEO]: {
    label: getLabel(POSITION_ENUM.CEO),
    tooltipTitle: "Chief Executive Directors",
  },
  [COLUMN.CFO]: {
    label: getLabel(POSITION_ENUM.CFO),
    tooltipTitle: "Chief Financial Officers and Financial Directors"
  },
  [COLUMN.DIRECTORS]: {
    label: "Total Directors",
    tooltipTitle: "Number of current main board directors",
  },
  [COLUMN.EXECS]: {
    label: getLabel(POSITION_ENUM.OTHER_EXEC_DIRECTOR),
    tooltipTitle: "Executive directors, other than CEO and CFO",
  },
  [COLUMN.INDEP_NON_EXEC]: {
    label: getLabel(POSITION_ENUM.NON_EXEC_DIRECTOR),
    tooltipTitle: "Non-Executive Directors, excluding Chair and SID",
  },
  [COLUMN.NON_EXEC_CHAIR]: {
    label: getLabel(POSITION_ENUM.CHAIRMAN),
    tooltipTitle: "Chairs",
  },
  [COLUMN.OTHER_DIRS]: {
    label: getLabel(POSITION_ENUM.OTHER),
    tooltipTitle: "Directors who have not been classified",
  },
  [COLUMN.SECRETARY]: {
    label: getLabel(POSITION_ENUM.SECRETARY),
    tooltipTitle: "Company Secretaries"
  },
  [COLUMN.SID]: {
    label: getLabel(POSITION_ENUM.SENIOR_NON_EXEC),// senior independent director
    tooltipTitle: "Senior Independent Directors",
  },
  //#endregion BOARD

  [COLUMN.CODIRECTOR]: {
    label: JARGON.CODIRECTOR,
  },
  [COLUMN.CONNECTED_CO]: {
    label: JARGON.CONNECTED_CO,
  },
  [COLUMN.PREVIOUS_NAMES]: {
    label: "Previous names",
  },
  [COLUMN.HEADLINE]: {
    label: "Headline",
  },
  [COLUMN.AI_SUMMARY]: {
    label: "AI Summary",
  },
  [COLUMN.PUBLICATION_DATE]: {
    label: "Date",
  },
  [COLUMN.CATEGORY]: {
    label: "Category",
  },
  [COLUMN.TYPE]: {
    label: "Type",
  },

  //#region committees
  [COLUMN.COMMITTEE_ROLES]: {
    label: "Committee Roles",
  },
  [COLUMN.COMMITTEE.AUDIT.CHAIRS]: {
    label: "Audit Chair",
    tooltipTitle: "Chair of the Audit Committee",
  },
  [COLUMN.COMMITTEE.AUDIT.MEMBER]: {
    label: "Audit Mbr.",
    tooltipTitle: "Member of the Audit Committee",
  },
  [COLUMN.COMMITTEE.AUDIT.MEMBERS]: {
    label: "Audit Mbrs.",
    tooltipTitle: "Members of the Audit Committee (other than Chair)",
  },
  [COLUMN.COMMITTEE.REMUNERATION.CHAIRS]: {
    label: "RemCo Chair",
    tooltipTitle: "Chair of the Remuneration Committee",
  },
  [COLUMN.COMMITTEE.REMUNERATION.MEMBER]: {
    label: "RemCo Mbr.",
    tooltipTitle: "Member of the Remuneration Committee",
  },
  [COLUMN.COMMITTEE.REMUNERATION.MEMBERS]: {
    label: "RemCo Mbrs.",
    tooltipTitle: "Members of the Remuneration Committee (other than Chair)",
  },
  [COLUMN.COMMITTEE.NOMINATION.CHAIRS]: {
    label: "NomCo Chair",
    tooltipTitle: "Chair of the Nomination Committee",
  },
  [COLUMN.COMMITTEE.NOMINATION.MEMBER]: {
    label: "NomCo Mbr.",
    tooltipTitle: "Member of the Nomination Committee",
  },
  [COLUMN.COMMITTEE.NOMINATION.MEMBERS]: {
    label: "NomCo Mbrs.",
    tooltipTitle: "Members of the Nomination Committee (other than Chair)",
  },
}

// If portrait, abbreviate "Members" to "Mbrs.
export const abbreviateMembers = ({ committee, portrait }) => {
  if (portrait)
    return LEGEND[committee.MEMBERS].label.replace("Members", "Mbrs.")
  else null
}
//#endregion committees
